import { z } from 'zod'

import type {
  BrowserNotificationPreferences,
  UserPreferenceData,
} from '@scrambl/shared'
import {
  isValidUUID,
  PreferenceContextEnum,
  PreferenceScopeEnum,
} from '@scrambl/shared'

const categories = z
  .object({
    tasks: z.boolean().optional(),
    comments: z.boolean().optional(),
    mentions: z.boolean().optional(),
    assignments: z.boolean().optional(),
    deadlines: z.boolean().optional(),
    system: z.boolean().optional(),
  })
  .strict()
const desktop = z
  .object({
    showBadgeCount: z.boolean().optional(),
    soundName: z.string().max(64).optional(),
    enableActions: z.boolean().optional(),
    persistNotifications: z.boolean().optional(),
    groupNotifications: z.boolean().optional(),
  })
  .strict()
const display = z
  .object({
    showWhenTabActive: z.boolean().optional(),
    showWhenTabInactive: z.boolean().optional(),
    playSound: z.boolean().optional(),
    autoDismissMs: z.number().int().min(0).max(3600000).optional(),
  })
  .strict()
const body = display
  .extend({
    enabled: z.boolean(),
    userDisabled: z.boolean().optional(),
    permissionRequested: z.boolean(),
    permissionStatus: z.enum(['granted', 'denied', 'default']).optional(),
    enabledTypes: categories.passthrough().optional(),
    desktopSettings: desktop.passthrough().optional(),
  })
  .passthrough()
export class BrowserPreferenceContractError extends Error {
  constructor() {
    super('Notification settings are unavailable')
    this.name = 'BrowserPreferenceContractError'
  }
}
function parse<T>(schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value)
  if (!result.success) throw new BrowserPreferenceContractError()
  return result.data
}
export const browserPreferenceInput = {
  body: (value: unknown): BrowserNotificationPreferences => {
    const parsed = parse(body, value)
    return {
      ...parsed,
      enabled: parsed.enabled,
      permissionRequested: parsed.permissionRequested,
    }
  },
  categories: (value: unknown) => parse(categories, value),
  desktop: (value: unknown) => parse(desktop, value),
  display: (value: unknown) => parse(display, value),
}
/** Personal notification choices must never inherit organization defaults. */
export function assertBrowserPreferenceRow(
  row: UserPreferenceData,
  ownerUserId: string | null,
  organizationId: string | null,
) {
  if (
    !row ||
    !isValidUUID(row.id) ||
    row.userId !== ownerUserId ||
    row.organizationId !== organizationId ||
    row.scope !== PreferenceScopeEnum.USER ||
    row.context !== PreferenceContextEnum.NOTIFICATIONS ||
    row.moduleType != null ||
    (row.surface != null && row.surface !== '') ||
    row.isActive !== true
  )
    throw new BrowserPreferenceContractError()
  browserPreferenceInput.body(row.preferences)
}
